import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import CustomDialog from '../custom-dialog/custom-dialog';

interface PasswordDisplayDialogProps {
  open: boolean;
  onClose: () => void;
  password: string;
  title?: string;
  message?: string;
}

function PasswordDisplayDialog({
  open,
  onClose,
  password,
  title = 'New Password Generated',
  message = 'Please save this password. You will not be able to see it again.',
}: PasswordDisplayDialogProps) {
  const [showPassword, setShowPassword] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CustomDialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {message}
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              fontFamily: 'monospace',
              fontWeight: 'bold',
              letterSpacing: 1,
            }}
          >
            {showPassword ? password : '••••••••••'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
          <IconButton
            size="small"
            color={copied ? 'success' : 'primary'}
            onClick={handleCopyPassword}
          >
            <ContentCopy />
          </IconButton>
        </Box>
        {copied && (
          <Typography variant="caption" color="success.main" sx={{ mt: 1 }}>
            Password copied to clipboard!
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}

export default PasswordDisplayDialog;
